import React, { useState } from 'react';
import '../css/AddService.css';

const AddHolidayModal = ({ theme, isOpen, onClose, onSubmit, drivers, admins, warehouseStaff = [], selectedDate }) => {
    const [formData, setFormData] = useState({
        selectedUser: '',
        holidayType: '',
        notes: '',
    });

    const [errors, setErrors] = useState('');

    // Helper function to format date as DD/MM/YYYY
    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    };

    // Combine drivers, admins and warehouse staff into one list
    const allUsers = [
        ...(drivers || []).map(driver => ({ ...driver, type: 'driver', displayName: `${driver.name} ${driver.surname} (Driver)` })),
        ...(admins || []).map(admin => ({ ...admin, type: 'admin', displayName: `${admin.name} ${admin.surname} (Admin)` })),
        ...(warehouseStaff || []).map(staff => ({ ...staff, type: 'warehouse', displayName: `${staff.name} ${staff.surname} (Warehouse)` }))
    ];

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const validateForm = () => {
        let errorMessage = '';
    
        if (!formData.selectedUser) {
            errorMessage = 'Please select a user.';
        } else if (!formData.holidayType) {
            errorMessage = 'Please select a holiday type.';
        } else if (!selectedDate) {
            errorMessage = 'No date selected.';
        }
    
        setErrors(errorMessage);
    
        // Return true if the form is valid (no errors)
        return !errorMessage;
    };

    const handleSubmit = () => {
        if (validateForm()) {
            console.log('Form data selectedUser:', formData.selectedUser);
            console.log('All users:', allUsers);
            
            // Try both string and number comparison
            const selectedUser = allUsers.find(user => 
                user.id === formData.selectedUser || 
                user.id === parseInt(formData.selectedUser) ||
                String(user.id) === formData.selectedUser
            );
            console.log('Found selected user:', selectedUser);
            
            if (!selectedUser) {
                setErrors('Please select a valid user');
                return;
            }
            
            const submitData = {
                startDate: selectedDate,  // Use the selected day from calendar
                endDate: selectedDate,    // Same day for single holiday
                holidayType: formData.holidayType,
                notes: formData.notes,
                userType: selectedUser.type,
                userId: selectedUser.id,
            };
            console.log('Submitting holiday data:', submitData);
            onSubmit(submitData);
            setFormData({
                selectedUser: '',
                holidayType: '',
                notes: '',
            });
            setErrors('');
            onClose();
        }
    };

    const handleClose = () => {
        setFormData({
            selectedUser: '',
            holidayType: '',
            notes: '',
        });
        setErrors('');
        onClose();
    };

    if (!isOpen) return null;

    const isDark = theme === 'dark';

    return (
        <div id={theme} className="overlay">
            <div className="modal-container">
                <h2>Add New Holiday</h2>
                <form className="form">

                    {/* Select User */}
                    <div className="input-group">
                        <label>User</label>
                        <select
                            name="selectedUser"
                            value={formData.selectedUser}
                            onChange={handleInputChange}
                            className="input"
                        >
                            <option value="">Select User</option>
                            {allUsers.map((user) => (
                                <option key={`${user.type}-${user.id}`} value={user.id}>
                                    {user.displayName}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Select Holiday Types */}
                    <div className="input-group">
                        <label>Holiday Type</label>
                        <div className="service-buttons-container">
                            {['Annual Leave', 'Sick Leave', 'Emergency Leave', 'Unpaid Leave'].map((holidayType, index) => {
                                const value = holidayType.toLowerCase().replace(' ', '_');
                                return (
                                    <button
                                        key={holidayType}
                                        className="service-button"
                                        style={{
                                            border: formData.holidayType === value ? '2px solid blue' : '1px solid #ccc',
                                            backgroundColor:
                                                formData.holidayType === value
                                                ? 'rgba(173, 216, 230, 0.4)'
                                                : theme === 'dark'
                                                ? 'rgba(255, 255, 255, 0.1)'
                                                : 'rgba(0, 0, 0, 0.1)',
                                            color: formData.holidayType === value ? 'blue' : 'inherit',
                                        }}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            setFormData(prev => ({ ...prev, holidayType: value }));
                                        }}
                                    >
                                        {holidayType}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Selected Date Display */}
                    <div className="input-group">
                        <label>Holiday Date</label>
                        <div className="input" style={{ 
                            backgroundColor: isDark ? 'rgba(255, 255, 255, 0.1)' : '#f5f5f5', 
                            border: isDark ? '1px solid #555' : '1px solid #ccc',
                            color: isDark ? '#fff' : '#333',
                            padding: '8px 12px',
                            borderRadius: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            fontWeight: 'bold'
                        }}>
                            {selectedDate ? formatDate(selectedDate) : 'No date selected'}
                        </div>
                        <small style={{ color: isDark ? '#ccc' : '#666', fontSize: '12px' }}>
                            Click on a different day in the calendar to change the date
                        </small>
                    </div>

                    {/* Notes */}
                    <div className="input-group">
                        <label>Notes (Optional)</label>
                        <textarea
                            name="notes"
                            value={formData.notes}
                            onChange={handleInputChange}
                            className="input"
                            rows="3"
                            placeholder="Enter any additional notes..."
                        />
                    </div>

                    {errors && (
                        <div>
                            <span style={{ color: 'red' }}>{errors}</span>
                        </div>
                    )}
                </form>

                {/* Buttons */}
                <div className="button-group">
                    <button onClick={handleSubmit} className="submit-button">
                        Add Holiday
                    </button>
                    <button onClick={handleClose} className="close-button">
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AddHolidayModal;